import {compose, pick} from 'ramda'
import {AdminExistingUserView, NewTradePartnerUserView} from '@modifi/onboarding-api-clients'
import {TRADE_PARTNER_INPUT_FIELD_NAMES} from './constants'

const pickTradePartnerProperties = pick(TRADE_PARTNER_INPUT_FIELD_NAMES)

const propertyToInputFieldName = (propertyName: string): string => `tradePartner.${propertyName}`

const mapResponseToFormData = (response: Record<string, any>): Record<string, any> =>
  Object.fromEntries(
    Object.entries(response).map(([propertyName, value]) => [
      propertyToInputFieldName(propertyName),
      value,
    ])
  )

// the response holds the limit as an amount object, the form keeps the amount and
// its currency in two separate fields
const flattenExpectedFinancingLimit = ({
  expectedFinancingLimit,
  ...response
}: Record<string, any>): Record<string, any> => ({
  ...response,
  ...(expectedFinancingLimit
    ? {
        expectedFinancingLimit: expectedFinancingLimit.amount,
        tradingCurrency: expectedFinancingLimit.currency,
      }
    : {}),
})

export const mapTradePartnerToFormData = compose(
  pickTradePartnerProperties,
  mapResponseToFormData,
  flattenExpectedFinancingLimit
)

export const formatContactPersonChanges = (changes: Record<string, any>): any => {
  let formattedObject = {}
  Object.keys(changes).forEach(changeKey => {
    // if the value is null that means it was deleted since we explicitly set it
    if (changes?.[changeKey] !== null) {
      // id will come after '-' in the key
      const changeKeyParts = changeKey?.split('-')
      const updatedId = changeKeyParts?.[1]
      const propertyKey = changeKeyParts?.[0]

      // @ts-expect-error will only be empty in first iteration
      if (formattedObject?.[updatedId]) {
        // @ts-expect-error will only be empty in first iteration
        formattedObject[updatedId] = {
          // @ts-expect-error will only be empty in first iteration
          ...formattedObject[updatedId],
          [propertyKey]: changes[changeKey],
        }
      } else {
        formattedObject = {
          ...formattedObject,
          [updatedId]: {
            [propertyKey]: changes[changeKey],
          },
        }
      }
    }
  })

  const newContactPersons = Object.values(formattedObject)?.map(
    // @ts-expect-error to be fixed
    (contactPerson: Record<string, any>) => {
      let updatedContactPersonObject = {}
      Object.keys(contactPerson).forEach(contactPersonKey => {
        const formattedKey = contactPersonKey?.replace('tradePartner.contactPerson', '')
        const keyInCamelCase = formattedKey?.charAt(0)?.toLowerCase() + formattedKey?.slice(1)
        updatedContactPersonObject = {
          ...updatedContactPersonObject,
          [keyInCamelCase]: contactPerson[contactPersonKey],
        }
      })
      return updatedContactPersonObject
    }
  )
  return newContactPersons
}

export const formatExistingPersonsForPutRequest = (
  existingPersons: AdminExistingUserView[],
  contactPersonsToDelete: string[]
): Required<NewTradePartnerUserView>[] =>
  existingPersons.reduce((formattedExistingPersons, person) => {
    if (contactPersonsToDelete?.includes(person.externalId!)) {
      return formattedExistingPersons
    }
    return [
      ...formattedExistingPersons,
      {
        email: person?.email,
        firstName: person?.firstName,
        lastName: person?.lastName,
        phone: person?.phone,
        salutation: person?.salutation,
        position: person?.position,
      },
    ]
  }, [] as any)

export const getRiskCountries = (countries: Record<string, any>[], riskLevel: string): string[] =>
  countries
    ?.filter(country => country?.buyerRiskLevel === riskLevel)
    .map(country => country.alpha2) as string[]

export const countrySpecific = (key: string, targetCountry?: string | null): string[] | string =>
  targetCountry ? [`${key}.${targetCountry}`, key] : key
